<script setup>
    import { ref, computed, onMounted, onUnmounted, onUpdated } from 'vue';
    import Bubble from '@/components/Bubble.vue';

    import { useRoute } from 'vue-router'
    const route = useRoute()

    import InlineSpinner from '@/components/InlineSpinner.vue'

    import { useBusinessPartnersStore } from '@/stores/businessPartners'
    const businessPartnerStore = useBusinessPartnersStore();

    import { usePerformanceStore } from '@/stores/performance'
    const performanceStore = usePerformanceStore();

    const managerHost = import.meta.env.VITE_MANAGER_HOST;

    import { useUserStore } from '@/stores/user';
    const userStore = useUserStore();

    import { useActivityStore } from '@/stores/activity'
    const activityStore = useActivityStore();

    import { useFunctionsStore } from '@/stores/functions'
    const functionsStore = useFunctionsStore()

    import userAvatar from '@/components/userAvatar.vue'
    import iconWvl from '@/assets/imgs/icons/icon-wvl.png'
    import iconVot from '@/assets/imgs/icons/icon-vot.png'

    const idOwnTeam = computed(() => {
        if (!userStore.isSalesTeamManager) return null;
        return userStore.currentSelection.campaign.roles.find((role) => ["SMA:salesTeamManager"].includes(role.name)).team.id;
    })

    const percentageValueWeek = computed(() => {
        if (!activityStore.week) return 0;
        if (activityStore.week[0]?.actualValues?.salesEuro === 0 && activityStore.week[0]?.plannedValues?.salesEuro === 0) {
            return 0;
        }
        const value = Math.round((activityStore.week[0]?.actualValues?.salesEuro / activityStore.week[0]?.plannedValues?.salesEuro) * 100)
        return isNaN(value) ? 0 : value;
    });

    const percentageValueMonth = computed(() => {
        // if (!activityStore.month) return 0;
        // if (activityStore.month[0]?.actualValues?.salesEuro === 0 && activityStore.month[0]?.plannedValues?.salesEuro === 0) {
        //     return 0;
        // }
        // const value = Math.floor((activityStore.month[0]?.actualValues?.salesEuro / activityStore.month[0]?.plannedValues?.salesEuro) * 100)
        // return isNaN(value) ? 0 : value;

        if (!activityStore.month) return 0;
        if (activityStore.month[0]?.actualValues?.salesEuro === 0 && activityStore.month[0]?.plannedValues?.salesEuro === 0) {
            return 0;
        }
        const value = activityStore.month.level.value
        return isNaN(value) ? 0 : value;
    });

    // Progress bars are intentionally disabled for now.
    // Re-enable for next year when the feature goes live again.
    // const reminderProgressBars = computed(() => ([
    //     { label: 'Woche', percent: Math.max(0, Math.min(100, percentageValueWeek.value)) },
    //     { label: 'Monat', percent: Math.max(0, Math.min(100, percentageValueMonth.value)) },
    // ]));

    const isActive = ref(false);

    function toggleActive() {
        // console.log("cklick")
        isActive.value = !isActive.value;
    }

    // ── Bubble mode (all pages except Dashboard) ────────────────────────────
    const isDashboard = computed(() => route.name === 'Dashboard')
    const showLegacyFooterReminder = false
    const isBubbleOpen = ref(false)

    function getReminderIcon(typeKey) {
        return typeKey === 'VorOrtTermine' ? iconVot : iconWvl
    }

    const nowTimestamp = ref(Date.now())

    function parseFollowUpDate(value) {
        if (!value || typeof value !== 'string') return null
        // Backend date format is usually "YYYY-MM-DD HH:mm:ss"; parse it as local time.
        const parsed = new Date(value.replace(' ', 'T'))
        if (!Number.isNaN(parsed.getTime())) return parsed

        const fallback = new Date(value)
        return Number.isNaN(fallback.getTime()) ? null : fallback
    }

    function getLiveRemainingMinutes(item) {
        if (!item) return null

        const followUpDate = parseFollowUpDate(item.businessPartner?.salesPitch?.dateFollowUp)
        if (followUpDate) {
            return Math.max(0, Math.ceil((followUpDate.getTime() - nowTimestamp.value) / 60000))
        }

        return item.remainingMinutes ?? null
    }

    function getLiveDeltaMinutes(item) {
        if (!item) return null

        const followUpDate = parseFollowUpDate(item.businessPartner?.salesPitch?.dateFollowUp)
        if (followUpDate) {
            return Math.ceil((followUpDate.getTime() - nowTimestamp.value) / 60000)
        }

        return item.remainingMinutes ?? null
    }

    function getEscalationLevel(item) {
        const delta = getLiveDeltaMinutes(item)
        if (delta == null) return 'normal'
        if (delta < -60) return 'critical'
        if (delta < -15) return 'high'
        if (delta < 0) return 'due'
        if (delta <= 30) return 'soon'
        return 'normal'
    }

    function formatMinutes(mins) {
        if (mins == null) return '--'
        const absMin = Math.abs(mins)
        if (absMin < 60) return `${absMin} Min.`
        const h = Math.floor(absMin / 60)
        const m = absMin % 60
        return m === 0 ? `${h} Std.` : `${h} Std. ${m} Min.`
    }

    function getEscalationText(item) {
        const delta = getLiveDeltaMinutes(item)
        if (delta == null) return ''
        if (delta < 0) return `Überfällig ${formatMinutes(Math.abs(delta))}`
        if (delta <= 30) return `In ${formatMinutes(delta)}`
        return ''
    }

    function isSameLocalDay(date, referenceDate) {
        return date.getFullYear() === referenceDate.getFullYear()
            && date.getMonth() === referenceDate.getMonth()
            && date.getDate() === referenceDate.getDate()
    }

    function buildReminderEntry(typeKey, label) {
        const items = businessPartnerStore.reminders[typeKey] || []
        const item = items[0]
        if (!item) return null

        return {
            typeKey,
            label,
            time: item.businessPartner.salesPitch.dateFollowUp?.split(' ')[1]?.slice(0, 5) ?? '',
            minutes: getLiveRemainingMinutes(item),
            name: item.businessPartner.name,
        }
    }

    const miniReminderItems = computed(() => {
        const items = [
            buildReminderEntry('Wiedervorlagen', 'WVL'),
            buildReminderEntry('VorOrtTermine', 'PT'),
        ].filter(Boolean)

        return items.sort((a, b) => {
            const aMinutes = a.minutes ?? Number.POSITIVE_INFINITY
            const bMinutes = b.minutes ?? Number.POSITIVE_INFINITY
            return aMinutes - bMinutes
        })
    })

    const primaryReminder = computed(() => miniReminderItems.value[0] || null)
    const isUrgent = computed(() => primaryReminder.value?.minutes != null && primaryReminder.value.minutes <= 30)

    const latestVorOrtReminder = computed(() => {
        const items = businessPartnerStore.reminders['VorOrtTermine'] || []
        return items.length ? items[0] : null
    })

    const remainingVorOrtReminders = computed(() => {
        const items = businessPartnerStore.reminders['VorOrtTermine'] || []
        return items.length > 1 ? items.slice(1) : []
    })

    const teamReminderSummary = computed(() => {
        const now = new Date(nowTimestamp.value)
        const list = [
            ...(businessPartnerStore.reminders['Wiedervorlagen'] || []).map(item => ({ item, type: 'WVL' })),
            ...(businessPartnerStore.reminders['VorOrtTermine'] || []).map(item => ({ item, type: 'PT' })),
        ]

        const withDates = list.map(entry => {
            const followUpDate = parseFollowUpDate(entry.item.businessPartner?.salesPitch?.dateFollowUp)
            const delta = getLiveDeltaMinutes(entry.item)
            return { ...entry, followUpDate, delta }
        })

        const overdue = withDates.filter(entry => entry.delta != null && entry.delta < 0).length
        const dueToday = withDates.filter(entry => entry.followUpDate && isSameLocalDay(entry.followUpDate, now)).length
        const dueSoon = withDates.filter(entry => entry.delta != null && entry.delta >= 0 && entry.delta <= 30).length
        const ptToday = withDates.filter(entry => entry.type === 'PT' && entry.followUpDate && isSameLocalDay(entry.followUpDate, now)).length
        const maxOverdueMinutes = withDates.reduce((max, entry) => {
            if (entry.delta == null || entry.delta >= 0) return max
            return Math.max(max, Math.abs(entry.delta))
        }, 0)

        return {
            total: withDates.length,
            overdue,
            dueToday,
            dueSoon,
            ptToday,
            maxOverdueMinutes,
        }
    })

    // manages the selection of a salesAgent or salesTeam in the management view
    async function handleSelectSalesAgent(idSelection) {
        // console.log("handleSelectSalesAgent", idSelection)
        if (idSelection == -1) {await userStore.selectCompany()}
        if (idSelection.startsWith('salesAgent:')) await userStore.selectSalesAgent(idSelection.replace('salesAgent:', ''))
        if (idSelection.startsWith('salesTeam:')) await userStore.selectSalesTeam(idSelection.replace('salesTeam:', ''))
    }


    let timerReminder = null;
    let timerCountdown = null;
    onMounted(() => {
        // Nur noch Intervall, initialer Abruf erfolgt nach Login im userStore
        timerReminder = setInterval(async () => {
            businessPartnerStore.getByFilter(businessPartnerStore.namedFilter('Reminder:VorOrtTermine'), [], true);
            businessPartnerStore.getByFilter(businessPartnerStore.namedFilter('Reminder:Wiedervorlagen'), [], true);
        }, 1000 * 60 * 5); // alle 300 Sekunden (5 Minuten)

        // Keep minute countdowns reactive without querying the backend every 30 seconds.
        timerCountdown = setInterval(() => {
            nowTimestamp.value = Date.now()
        }, 1000 * 30)

        updateFooterHeight();
        window.addEventListener('resize', updateFooterHeight);
    })

    onUpdated(() => {
        updateFooterHeight()
    })

    onUnmounted (() => {
        clearInterval(timerReminder)
        clearInterval(timerCountdown)
        // clearInterval(timerFollowUps)
        window.removeEventListener('resize', updateFooterHeight)
    })


    const footerRef = ref(null)
    function updateFooterHeight() {
        if (!isDashboard.value || !showLegacyFooterReminder) {
            // On sub-pages the footer is hidden; reset reserved space so the list fills the screen
            functionsStore.setFooterHeight(0)
            if (typeof document !== 'undefined') {
                document.documentElement.style.setProperty('--reminder-height', '0px')
            }
            return
        }
        if (footerRef.value) {
            const height = footerRef.value.getBoundingClientRect().height
            functionsStore.setFooterHeight(height)
            if (typeof document !== 'undefined') {
                document.documentElement.style.setProperty('--reminder-height', `${Math.ceil(height)}px`)
            }
        }
    }

    function getCurrentMonth() {
        const currentDate = new Date()
        const monthNames = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"]
        const month = monthNames[currentDate.getMonth()]
        const year = currentDate.getFullYear().toString().slice(-2)
        return `${month}. ${year}`
    }

    const currentMonth = ref(getCurrentMonth())

    const currentMonthBubble = computed(() => {
        // Robust fallback: always return a valid bubble object
        const bubble = {
            level: 0,
            actualValue: 0,
            plannedValue: 0,
        };
        // Defensive: check for valid activity and month array
        const months = activityStore.activity && Array.isArray(activityStore.activity.month) ? activityStore.activity.month : [];
        const idCurrentMonth = activityStore.activity && activityStore.activity.idCurrentMonth;
        let month = null;
        if (months.length && idCurrentMonth != null) {
            month = months.find(item => item.id === idCurrentMonth);
        }
        // If no month found, but months exist, use the first as fallback
        if (!month && months.length) {
            month = months[0];
        }
        // If still no month, return default bubble
        if (!month) return bubble;
        if (userStore.currentSelection.campaign.id <= 22) {
            return {
                level: month.level?.points || 0,
                actualValue: month.actualValues?.salesEuro || 0,
                plannedValue: month.plannedValues?.salesEuro || 0,
            };
        } else if (userStore.currentSelection.campaign?.id > 22) {
            return {
                level: performanceStore.performance?.halftime?.status?.commissionPoints ? Number(performanceStore.performance.halftime.status.commissionPoints) : 0,
                actualValue: performanceStore.performance?.halftime?.status?.commissionPoints ? Number(performanceStore.performance.halftime.status.commissionPoints) : 0,
                plannedValue: 1,
            };
        }
        return bubble;
    });

      onMounted(() => {
        DruidWebchat_v2.init({
            botId: "7dec8607-29ef-44f5-3485-08dd938184cd", // DEV
            // botId: "523f15ee-854e-4c73-952a-08de56c85d08", // PROD
            baseUrl: "https://druidapi.druidplatform.com",
            queryParams: `UserId=${userStore.sugar?.id}`,
        });
    });
</script>

<template>
  <!-- ── Dashboard: full footer reminder ─────────────────────────────── -->
    <footer v-if="isDashboard && showLegacyFooterReminder" class="max-width" id="reminder" ref="footerRef">
        <div class="reminder-inner-wrapper">
            <div class="wrapper">
                <div class="follow-up-wrapper">
                    <div>
                        <div class="headline-wrapper">
                            <div>
                                <img src="@/assets/imgs/icons/icon-wvl.png" alt="Wiedervorlagen Icon"> 
                                <p>Wiedervorlagen:</p>
                            </div>
                        </div>
                        <ul v-if="businessPartnerStore.reminders['Wiedervorlagen'].length>0">
                            <li v-for="appointment of businessPartnerStore.reminders['Wiedervorlagen']" :key="appointment.businessPartner.id">
                                <router-link :to="{ name: 'BusinessPartner', query: { searchMode: 'GPNo', search: appointment.businessPartner.gpNo } }">
                                    {{ appointment.businessPartner.name }}
                                </router-link>
                                <span class="time">{{ appointment.businessPartner.salesPitch.dateFollowUp?.split(' ')[1].slice(0,5)}}</span>
                            </li>
                        </ul>
                        <ul class="no-appointments" v-else>
                            <li>
                                <span> Keine Wiedervorlagen mit Uhrzeit festgelegt! </span>
                            </li>
                        </ul>
                    </div>
                </div>
                <hr>
                <div class="appointments-wrapper" v-if="businessPartnerStore.reminders['VorOrtTermine'].length>0">
                    <div>
                    <div v-for="(appointment, index) of businessPartnerStore.reminders['VorOrtTermine']" class="follow-ups" :key="appointment.businessPartner.id">
                        <div v-if="index === 0" class="primary">
                            <div class="headline-wrapper">
                                <div>            
                                    <img src="@/assets/imgs/icons/icon-vot.png" alt="Wiedervorlagen Icon"> 
                                    <p>Verbindliche Termine:</p>
                                </div>
                            </div>
                            <ul>
                                <li>
                                    <span>
                                        <router-link :to="{ name: 'BusinessPartner', query: { searchMode: 'GPNo', search: appointment.businessPartner.gpNo } }">
                                            {{ appointment.businessPartner.name }}
                                        </router-link>
                                        <span class="clock">{{ appointment.businessPartner.salesPitch.dateFollowUp?.split(' ')[1].slice(0,5)}}</span>
                                    </span>
                                </li>
                                <li>
                                    <span>
                                        {{ appointment.businessPartner.addressList[0].street }} {{ appointment.businessPartner.addressList[0].houseNumber }}, 
                                        {{ appointment.businessPartner.addressList[0].postalCode }} {{ appointment.businessPartner.addressList[0].city.name }} 
                                    </span>
                                </li>
                                <li v-if="appointment.businessPartner.salesPitch.contact.firstName">
                                    <span>{{ appointment.businessPartner.salesPitch.contact.firstName }} {{ appointment.businessPartner.salesPitch.contact.lastName }}</span>
                                </li>
                                <li v-else>
                                    <span :style="{ opacity: 0.5 }"><i>Entscheider nicht hinterlegt</i></span>
                                </li>
                                <li v-if="appointment.businessPartner.salesPitch.comment">
                                    <span>{{ appointment.businessPartner.salesPitch.comment }}</span>
                                </li>
                                <li v-else>
                                    <span :style="{ opacity: 0.5 }"><i>Ohne Notiz</i></span>
                                </li>
                                <li>
                                    <a class="sellwerk-manager-logo" :href="managerHost + '/?sugId=' + appointment.businessPartner.idSugar" target="_blank">
                                        <button class="btn">
                                            <img src="@/assets/imgs/logos/sellwerk/sellwerk_manager_logo_small.svg" alt="SELLWERK Manager" style="height:32px;width:auto;vertical-align:middle;" />
                                        </button>
                                    </a>
                                    
                                </li>
                            </ul>
                        </div>
                        <div v-else class="secondary">
                            <hr v-if="index === 1" class="divider">
                            <div class="headline-wrapper">
                                <ul>
                                    <li>
                                        <span>
                                            <router-link :to="{ name: 'BusinessPartner', query: { searchMode: 'GPNo', search: appointment.businessPartner.gpNo } }">
                                                {{ appointment.businessPartner.name }}
                                            </router-link>
                                            <span>{{ appointment.businessPartner.salesPitch.dateFollowUp?.split(' ')[1].slice(0,5)}}</span>
                                        </span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
                </div>
                <div class="appointments-wrapper" v-else>
                    <div>
                        <div class="headline-wrapper">
                            <div>            
                                <img src="@/assets/imgs/icons/icon-vot.png" alt="Wiedervorlagen Icon"> 
                                <p>Verbindliche Termine:</p>
                            </div>
                        </div>
                        <ul class="no-appointments">
                            <li>
                                <span>Keinen Vor Ort Termin!</span>
                            </li>
                        </ul>
                    </div>
                </div>
                <!-- <hr> -->
            </div>
            <div class="circle-container-wrapper">
                <template v-if="activityStore.activity && Array.isArray(activityStore.activity.week) && activityStore.activity.week.length">
                    <bubble
                        :mode="'points'"
                        :points="Number(-1 * (((activityStore.activity.week?.find(item => item.id === 0)?.plannedValues?.appointments ?? 0) - (activityStore.activity.week?.find(item => item.id === 0)?.actualValues?.appointments ?? 0))))"
                        :percentValue="Number(100 * ((activityStore.activity.week?.find(item => item.id === 0)?.actualValues?.appointments ?? 0) / (activityStore.activity.week?.find(item => item.id === 0)?.plannedValues?.appointments || 1)))"
                        :forecastValue="100"
                    ></bubble>
                </template>
                <template v-else>
                    <bubble :mode="'points'" :points="0" :percentValue="0" :forecastValue="100" />
                </template>
                <template v-if="currentMonthBubble && (currentMonthBubble.actualValue !== 0 || currentMonthBubble.plannedValue !== 0)">
                    <bubble
                        :mode="'points'"
                        :points="Number(Number.parseFloat(currentMonthBubble.actualValue ?? 0).toFixed(2))"
                        :percentValue="Number(((currentMonthBubble.actualValue ?? 0) / (currentMonthBubble.plannedValue || 1) * 100))"
                        :forecastValue="100"
                    />
                </template>
                <template v-else>
                    <bubble :mode="'points'" :points="0" :percentValue="0" :forecastValue="100" />
                </template>
            </div>
            <div class="reminder-overlay">
                <!-- salesAgent view: show "Wiedervorlagen" -->
                <template v-if="userStore.filterOptions.isLoading === true">
                    <InlineSpinner size="lg" label="Daten werden geladen" layout="row"/>
                    <div class="loadingDescription">WVL wird geladen</div>
                </template>
                <div class="txt-wrapper" v-else-if="userStore.filterOptions.isLoading === false && !userStore.isManagement && !userStore.isSalesTeamManager && !userStore.isWinback" @click="toggleActive">
                    <img src="@/assets/imgs/reminder/icon-wvl.png" alt="Wiedervorlage"> 
                    <span v-if="businessPartnerStore.reminders['Wiedervorlagen'].length>0">
                        in {{ formatMinutes(getLiveRemainingMinutes(businessPartnerStore.reminders['Wiedervorlagen'][0])) }} ({{ businessPartnerStore.reminders['Wiedervorlagen'][0].businessPartner.salesPitch.dateFollowUp.split(' ')[1].slice(0,5)   }} Uhr)
                    </span>
                    <span v-else>keine künftigen WVL</span>
                </div>
                <!-- management view: allow for selecting a salesAgent or area -->
                <select v-else @change="(event) => handleSelectSalesAgent(event.target.value)">
                    <template v-if="userStore.isManagement">
                        <option :selected="userStore.currentSelection.scope === 'campaign'" value="-1">Gesamter Standort</option>    
                        <hr>
                        <optgroup label="Sales Offices">
                            <option v-for="salesTeam in userStore.filterOption?.salesTeams
                                .filter(option => option.id !== -1)
                                .sort((a, b) => a.name.localeCompare(b.name))"
                                :key="salesTeam.id"
                                :selected="userStore.currentSelection.salesTeam.id === salesTeam.id"
                                :value="`salesTeam:${salesTeam.id}`">
                                {{ salesTeam.name }}
                            </option>
                        </optgroup>
                        <br>
                        <hr>
                        <br>
                    </template>
                    <template v-if="userStore.isSalesTeamManager">
                        <optgroup label="Sales Office">
                            <option :value="`salesTeam:${idOwnTeam}`" :selected="userStore.currentSelection.scope==='salesTeam'">Gesamte Area</option>
                            <option v-for="salesAgent in userStore.filterOption?.salesAgents
                                .filter(agent => agent.teamId === idOwnTeam)
                                .sort((a, b) => a.firstName.localeCompare(b.firstName))"
                                :key="salesAgent.id"
                                :value="`salesAgent:${salesAgent.id}`"
                                :selected="userStore.currentSelection.scope==='salesAgent' && userStore.currentSelection?.salesAgent?.id === salesAgent.id">
                                {{ salesAgent.firstName }} {{ salesAgent.lastName }}
                            </option>
                        </optgroup>
                        <br>
                        <hr>
                        <br>
                    </template>
                    <optgroup label="Sales Manager">
                        <option v-for="salesAgent in userStore.filterOption?.salesAgents
                            .sort((a,b) => (a.firstName).localeCompare(b.firstName))
                            .filter(option => option.id!==-1)"
                            :key="salesAgent.id"
                            :value="`salesAgent:${salesAgent.id}`"
                            :selected="userStore.currentSelection.scope==='salesAgent' && userStore.currentSelection?.salesAgent?.id === salesAgent.id">
                            {{ salesAgent.firstName }} {{ salesAgent.lastName }}
                        </option>
                    </optgroup>
                </select>
                <div v-if="1+1===3" class="custom-select">
                <button
                    id="dropdown-button"
                    class="select-button"
                    role="combobox"
                    aria-label="select button"
                    aria-haspopup="listbox"
                    aria-expanded="false"
                    aria-controls="select-dropdown"
                >
                    <span class="selected-value">Open this select menu</span>
                    <span class="arrow"></span>
                </button>
                <ul
                    class="select-dropdown hidden"
                    role="listbox"
                    id="select-dropdown"
                    aria-labelledby="dropdown-button"
                >
                    <li role="option"><i class="bx bxl-github"></i> GitHub</li>
                    <li role="option"><i class="bx bxl-instagram"></i> Instagram</li>
                    <li role="option"><i class="bx bxl-facebook-circle"></i> Facebook</li>
                    <li role="option"><i class="bx bxl-linkedin-square"></i> LinkedIn</li>
                    <li role="option"><i class="bx bxl-twitter"></i> Twitter</li>
                    <li role="option"><i class="bx bxl-reddit"></i> Reddit</li>
                    <!-- Clear option to reset selection -->
                    <li role="option" data-value="clear">
                    <span>Clear selection</span>
                    </li>
                </ul>
                </div>
            </div>
            <div :class="['reminder-main', { 'active': isActive }]" @click="toggleActive">
                <template v-if="businessPartnerStore.reminders['VorOrtTermine'].length>0">
                    <span   data-aos="fade-right"
                            data-aos-easing="ease-out-sine"
                            data-aos-delay="150"
                            data-aos-offset="0"
                            data-aos-anchor="#reminder"
                    class="timer">{{ formatMinutes(getLiveRemainingMinutes(businessPartnerStore.reminders['VorOrtTermine'][0])) }} </span>
                    <div    data-aos="fade-in"
                            data-aos-easing="ease-out-sine"
                            data-aos-delay="50"
                            data-aos-offset="0"
                            data-aos-anchor="#reminder"
                    class="client-info">
                        <div class="client-info">
                            <span class="client-name">{{businessPartnerStore.reminders['VorOrtTermine'][0].businessPartner.name}}</span>
                            <span class="client-adress"><i><b>{{ businessPartnerStore.reminders['VorOrtTermine'][0].businessPartner.addressList[0].street }} {{ businessPartnerStore.reminders['VorOrtTermine'][0].businessPartner.addressList[0].houseNumber }}, {{ businessPartnerStore.reminders['VorOrtTermine'][0].businessPartner.addressList[0].postalCode }} {{ businessPartnerStore.reminders['VorOrtTermine'][0].businessPartner.addressList[0].city.name }} </b></i></span>
                        </div>
                    </div>
                    <img    data-aos="fade-left"
                            data-aos-easing="ease-out-sine"
                            data-aos-delay="150"
                            data-aos-offset="0"
                            data-aos-anchor="#reminder" 
                    src="@/assets/imgs/icons/feather-more-vertical.svg" alt="Menu Icon"> 
                </template>
                <template v-else-if="((userStore.isManagement || userStore.isSalesTeamManager || userStore.isWinback) && userStore.currentSelection?.salesAgent?.id !==-1) || (!userStore.isManagement && !userStore.isSalesTeamManager && !userStore.isWinback)">
                    <span   data-aos="fade-right"
                            data-aos-easing="ease-out-sine"
                            data-aos-delay="1000"
                            data-aos-offset="0"
                            data-aos-anchor="#reminder"
                    class="timer">-- Min.</span>
                    <div    data-aos="fade-in"
                            data-aos-easing="ease-out-sine"
                            data-aos-delay="500"
                            data-aos-offset="0"
                            data-aos-anchor="#reminder"
                    class="client-info">
                        <span class="client-name">keine künftigen Termine</span>
                    </div>
                    <img    data-aos="fade-left"
                            data-aos-easing="ease-out-sine"
                            data-aos-delay="1000"
                            data-aos-offset="0"
                            data-aos-anchor="#reminder"
                    src="@/assets/imgs/icons/feather-more-vertical.svg" alt="Menu Icon"> 
                </template>
                <template v-else>
                   <span   data-aos="fade-right"
                            data-aos-easing="ease-out-sine"
                            data-aos-delay="1000"
                            data-aos-offset="0"
                            data-aos-anchor="#reminder"
                    class="timer">-- Min.</span>
                    <div    data-aos="fade-in"
                            data-aos-easing="ease-out-sine"
                            data-aos-delay="500"
                            data-aos-offset="0"
                            data-aos-anchor="#reminder"
                    class="client-info">
                        <span class="client-name">keine künftigen Termine</span>
                    </div>
                    <img    data-aos="fade-left"
                            data-aos-easing="ease-out-sine"
                            data-aos-delay="1000"
                            data-aos-offset="0"
                            data-aos-anchor="#reminder"
                    src="@/assets/imgs/icons/feather-more-vertical.svg" alt="Menu Icon"> 
                </template>
            </div>
            <!-- <div class='wave-wrapper'>
                <div class='wave -one'></div>
                <div class='wave -two'></div>
                <div class='wave -three'></div>
            </div> -->
        </div>
        <!-- <div class="footerNavigation">
            <div class="max-width">
                <div class="icon-wrapper">
                    <span class="number-label">1</span>
                    <img src="@/assets/imgs/icons/house-solid.svg" alt="Home Icon">
                    <p>Home</p>
                </div>
                <div class="icon-wrapper">
                    <img src="@/assets/imgs/icons/magnifying-glass-solid.svg" alt="Home Icon">
                    <p>Suche</p>
                </div>
                <div class="icon-wrapper">
                    <img src="@/assets/imgs/icons/square-plus-regular.svg" alt="Home Icon">
                    <p>Neu</p>
                </div>
                <div class="icon-wrapper">
                    <span class="number-label">3</span>
                    <img src="@/assets/imgs/icons/bell-regular.svg" alt="Home Icon">
                    <p>Feedback</p>
                </div>
                <div class="icon-wrapper">
                    <img src="@/assets/imgs/icons/list-solid.svg" alt="Home Icon">
                    <p>Listen</p>
                </div>
            </div>
        </div> -->
    </footer>

    <!-- ── Floating bubble on all pages ────────────────────────────── -->
    <div class="reminder-float">

    <!-- Expandable panel, slides up from the button -->
    <transition name="rb-panel">
      <div v-if="isBubbleOpen" class="rb-panel">
        <div class="rb-panel-header">
          <span>Reminder</span>
          <button class="rb-close" @click="isBubbleOpen = false" aria-label="Schließen">✕</button>
        </div>
        <div class="rb-panel-body">
                    <!-- Progress bars intentionally hidden until next year release -->
                    <!--
                    <div class="rb-progress-stack">
                        <div v-for="bar in reminderProgressBars" :key="bar.label" class="rb-progress-item">
                            <div class="rb-progress-headline">
                                <span class="rb-progress-label">{{ bar.label }}</span>
                                <span
                                    class="rb-progress-badge"
                                    :class="bar.percent >= 80 ? 'rb-progress-badge--good' : bar.percent >= 50 ? 'rb-progress-badge--mid' : 'rb-progress-badge--low'"
                                >{{ Math.round(bar.percent) }}%</span>
                            </div>
                            <div class="rb-bar-track">
                                <div
                                    class="rb-bar-fill"
                                    :class="bar.percent >= 80 ? 'rb-bar-fill--good' : bar.percent >= 50 ? 'rb-bar-fill--mid' : 'rb-bar-fill--low'"
                                    :style="{ width: Math.max(4, Math.min(100, bar.percent)) + '%' }"
                                ></div>
                            </div>
                        </div>
                    </div>
                    -->

          <hr class="rb-divider" />

          <!-- WVL -->
          <div class="rb-section">
                        <div class="rb-section-title" title="Wiedervorlagen">
              <img src="@/assets/imgs/icons/icon-wvl.png" class="rb-icon" alt="" />
                            Wiedervorlagen
            </div>
            <ul class="rb-list" v-if="businessPartnerStore.reminders['Wiedervorlagen'].length">
                            <li v-for="app of businessPartnerStore.reminders['Wiedervorlagen']" :key="app.businessPartner.id" :class="['rb-list-item', `rb-list-item--${getEscalationLevel(app)}`]">
                <router-link
                  :to="{ name: 'BusinessPartner', query: { searchMode: 'GPNo', search: app.businessPartner.gpNo } }"
                  @click="isBubbleOpen = false"
                >{{ app.businessPartner.name }}</router-link>
                                <span class="rb-list-meta">
                                    <span v-if="getEscalationText(app)" :class="['rb-escalation', `rb-escalation--${getEscalationLevel(app)}`]">{{ getEscalationText(app) }}</span>
                                    <span class="rb-time">{{ app.businessPartner.salesPitch.dateFollowUp?.split(' ')[1]?.slice(0,5) }}</span>
                                </span>
              </li>
            </ul>
            <p class="rb-empty" v-else>Keine Wiedervorlagen</p>
          </div>

          <hr class="rb-divider" />

          <!-- VorOrtTermine -->
          <div class="rb-section">
                        <div class="rb-section-title" title="Persönlich vor Ort">
              <img src="@/assets/imgs/icons/icon-vot.png" class="rb-icon" alt="" />
                            Persönlich vor Ort
            </div>
                        <div v-if="latestVorOrtReminder" class="rb-pt-feature">
                            <div class="rb-pt-headline">
                <router-link
                                    :to="{ name: 'BusinessPartner', query: { searchMode: 'GPNo', search: latestVorOrtReminder.businessPartner.gpNo } }"
                  @click="isBubbleOpen = false"
                                >{{ latestVorOrtReminder.businessPartner.name }}</router-link>
                                <span class="rb-list-meta">
                                    <span v-if="getEscalationText(latestVorOrtReminder)" :class="['rb-escalation', `rb-escalation--${getEscalationLevel(latestVorOrtReminder)}`]">{{ getEscalationText(latestVorOrtReminder) }}</span>
                                    <span class="rb-time">{{ latestVorOrtReminder.businessPartner.salesPitch.dateFollowUp?.split(' ')[1]?.slice(0,5) }}</span>
                                </span>
                            </div>

                            <p class="rb-pt-line" v-if="latestVorOrtReminder.businessPartner.addressList?.length">
                                {{ latestVorOrtReminder.businessPartner.addressList[0].street }} {{ latestVorOrtReminder.businessPartner.addressList[0].houseNumber }},
                                {{ latestVorOrtReminder.businessPartner.addressList[0].postalCode }} {{ latestVorOrtReminder.businessPartner.addressList[0].city?.name }}
                            </p>

                            <p class="rb-pt-line" v-if="latestVorOrtReminder.businessPartner.salesPitch?.contact?.firstName">
                                Entscheider: {{ latestVorOrtReminder.businessPartner.salesPitch.contact.firstName }} {{ latestVorOrtReminder.businessPartner.salesPitch.contact.lastName }}
                            </p>
                            <p class="rb-pt-line rb-pt-line--muted" v-else>Entscheider nicht hinterlegt</p>

                            <p class="rb-pt-line" v-if="latestVorOrtReminder.businessPartner.salesPitch?.comment">
                                Notiz: {{ latestVorOrtReminder.businessPartner.salesPitch.comment }}
                            </p>
                            <p class="rb-pt-line rb-pt-line--muted" v-else>Ohne Notiz</p>

                            <a
                                class="rb-manager-btn"
                                :href="managerHost + '/?sugId=' + latestVorOrtReminder.businessPartner.idSugar"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <img src="@/assets/imgs/logos/sellwerk/sellwerk_manager_logo_small.svg" alt="SELLWERK Manager" />
                            </a>
                        </div>

                        <ul class="rb-list" v-if="remainingVorOrtReminders.length">
                            <li v-for="app of remainingVorOrtReminders" :key="app.businessPartner.id" :class="['rb-list-item', `rb-list-item--${getEscalationLevel(app)}`]">
                                <router-link
                                    :to="{ name: 'BusinessPartner', query: { searchMode: 'GPNo', search: app.businessPartner.gpNo } }"
                                    @click="isBubbleOpen = false"
                                >{{ app.businessPartner.name }}</router-link>
                                <span class="rb-list-meta">
                                    <span v-if="getEscalationText(app)" :class="['rb-escalation', `rb-escalation--${getEscalationLevel(app)}`]">{{ getEscalationText(app) }}</span>
                                    <span class="rb-time">{{ app.businessPartner.salesPitch.dateFollowUp?.split(' ')[1]?.slice(0,5) }}</span>
                                </span>
                            </li>
                        </ul>
                        <p class="rb-empty" v-else-if="!latestVorOrtReminder">Keine Termine</p>
          </div>
        </div>
      </div>
    </transition>

    <!-- Floating pill button -->
    <button
            :class="['rb-btn', { 'rb-btn--urgent': isUrgent, 'rb-btn--critical': teamReminderSummary.overdue > 0, 'rb-btn--open': isBubbleOpen }]"
      @click="isBubbleOpen = !isBubbleOpen"
      :aria-label="isBubbleOpen ? 'Reminder schließen' : 'Reminder öffnen'"
    >
            <span v-if="miniReminderItems.length" class="rb-btn-info rb-btn-info--stack">
                <span v-for="item in miniReminderItems" :key="item.typeKey" class="rb-btn-line">
                                        <img :src="getReminderIcon(item.typeKey)" class="rb-line-icon" alt="" />
                    <span class="rb-btn-line-text">
                        <span class="rb-btn-label">{{ item.label }}</span>
                        <span class="rb-btn-mins">{{ item.minutes != null ? formatMinutes(item.minutes) : item.time }}</span>
                    </span>
                </span>
            </span>
            <span v-else class="rb-btn-info">
                <span class="rb-btn-label">Reminder</span>
            </span>
    </button>

  </div>
</template>